import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import type { KeptScan, ScanAgentApi } from './scan-agent';
import { fetchKeptPages, keptScansFor, nextHeldIndex } from './scan-kept';
import type { HeldPage } from './scan-pages';
import { currentScanSession } from './scan-session';

/** What the sheet's state carries for pages Paige Scan kept on disk. */
export interface KeptScanState {
  pages: HeldPage[];
  /** Scans whose pages no tab added or discarded (a tab that closed mid-scan), offered when the sheet opens. */
  kept: KeptScan[];
  /** The kept scan whose pages are being brought back now. */
  restoring: string | null;
  problem: string | null;
}

export interface ScanSpoolActions {
  /** A scan this sheet started: its copy on disk is released with the sheet's pages. */
  track: (scan: string) => void;
  /** The pages are in the job or discarded: Paige Scan deletes its copy of every scan this sheet holds. */
  releaseAll: () => void;
  /** Asks Paige Scan for kept scans to offer (nothing from an older Paige Scan). */
  offer: (api: ScanAgentApi) => Promise<void>;
  /** "Add them": the kept pages become the sheet's pages, as if just scanned. */
  addKept: (scan: KeptScan) => Promise<void>;
  /** "Discard": Paige Scan deletes them. */
  discardKept: (scan: KeptScan) => void;
}

const NOT_BACK = 'Those pages could not be brought back from Paige Scan. Try again.';

/**
 * Paige Scan's copy on disk of each scan (cpv6-parity I-12): every scan this sheet started or brought back is
 * released once its pages are added to the job, discarded, or the sheet closes without adding them; a scan no sheet
 * released (its tab closed, reloaded or crashed) is offered by the next sheet. Every call to Paige Scan here is best
 * effort: an older Paige Scan answers 404 and the sheet is exactly as it was.
 */
export function useScanSpools<S extends KeptScanState>(
  apiRef: RefObject<ScanAgentApi | null>,
  setState: Dispatch<SetStateAction<S>>,
): ScanSpoolActions {
  const own = useRef(new Set<string>());
  const track = useCallback((scan: string) => void own.current.add(scan), []);
  const releaseAll = useCallback(() => {
    const api = apiRef.current;
    for (const scan of own.current) void api?.release(scan).catch(() => undefined);
    own.current.clear();
  }, [apiRef]);
  // The sheet closed without adding the pages: Paige Scan's copy goes with them. (A closed tab runs no cleanup; its
  // scans stay on disk for the next sheet, which is the point.)
  useEffect(() => () => releaseAll(), [releaseAll]);
  const offer = useCallback(
    async (api: ScanAgentApi) => {
      const kept = await keptScansFor(api, currentScanSession()?.jobId ?? null, own.current);
      setState((prev) => ({ ...prev, kept }));
    },
    [setState],
  );
  const addKept = useAddKept(apiRef, own, setState);
  const discardKept = useCallback(
    (scan: KeptScan) => {
      void apiRef.current?.discardKept(scan.scan).catch(() => undefined);
      setState((prev) => ({ ...prev, kept: prev.kept.filter((k) => k.scan !== scan.scan) }));
    },
    [apiRef, setState],
  );
  return { track, releaseAll, offer, addKept, discardKept };
}

/** "Add them": the kept scan's pages, all of them or none, become the sheet's; the scan is then this sheet's to release. */
function useAddKept<S extends KeptScanState>(
  apiRef: RefObject<ScanAgentApi | null>,
  own: RefObject<Set<string>>,
  setState: Dispatch<SetStateAction<S>>,
): ScanSpoolActions['addKept'] {
  return useCallback(
    async (scan: KeptScan) => {
      const api = apiRef.current;
      if (!api) return;
      setState((prev) => ({ ...prev, restoring: scan.scan, problem: null }));
      try {
        const pages = await fetchKeptPages(api, scan);
        own.current.add(scan.scan);
        setState((prev) => ({
          ...prev,
          restoring: null,
          kept: prev.kept.filter((k) => k.scan !== scan.scan),
          pages: [...prev.pages, ...renumbered(pages, nextHeldIndex(prev.pages))],
        }));
      } catch {
        setState((prev) => ({ ...prev, restoring: null, problem: NOT_BACK }));
      }
    },
    [apiRef, own, setState],
  );
}

function renumbered(pages: HeldPage[], first: number): HeldPage[] {
  return pages.map((page, i) => ({ ...page, index: first + i }));
}
